import type { TranslocoService } from '@jsverse/transloco';

import type { BiometricKind, BiometricPrompt, BiometricUnlock } from '../../application';
import type { PublicJourneyUseCases } from './public-journey.dependencies';

/** The three ways the texts name it: the finger, the face, or the phone's biometrics. */
export const kindWord = (kind: BiometricKind): 'fingerprint' | 'face' | 'other' =>
  kind === 'fingerprint' || kind === 'face' ? kind : 'other';

/** What the system's prompt says, in the language being read. */
export const biometricPrompt = (i18n: TranslocoService): BiometricPrompt => ({
  title: i18n.translate('biometric.promptTitle'),
  subtitle: i18n.translate('biometric.promptSubtitle'),
  fallback: i18n.translate('biometric.usePhrase'),
});

/**
 * Turned on from Réglages with the space closed: the phrase is asked first, and the offer that
 * follows is made whatever « Plus tard » said before. Held by the module, which outlives the
 * settings screen while the unlock screen is shown.
 */
let askedFromSettings = false;

export const askBiometricAfterUnlock = (): void => {
  askedFromSettings = true;
};

/** Whether the owner asked from Réglages; asking takes it. */
export const takeBiometricAsked = (): boolean => {
  const asked = askedFromSettings;
  askedFromSettings = false;
  return asked;
};

/** The rules, on the apps; null on the web, where nothing of it is shown. Never throws. */
export const biometricOf = async (
  useCases: PublicJourneyUseCases,
): Promise<BiometricUnlock | null> => {
  if (useCases.biometric === null) return null;
  try {
    return await useCases.biometric();
  } catch {
    return null;
  }
};

/**
 * The phrase changed, was reset, or came back with a restored will: what the phone holds is
 * dropped, and offered again the next time the phrase opens the space.
 */
export const forgetBiometric = async (useCases: PublicJourneyUseCases): Promise<void> => {
  const biometric = await biometricOf(useCases);
  await biometric?.forget().catch(() => undefined);
};
